import { useQuery } from '@tanstack/react-query';

import { SECOND_IN_MS } from '@/lib/constants/time';
import { PollingInterval } from '@/modules/common/types/polling-interval';

import { getIntegrationStatus } from '../../../common/api/integration-status';

const LONG_POLLING_INTERVAL = 30 * SECOND_IN_MS; // 30 seconds - reasonable interval for status updates
const SHORT_POLLING_INTERVAL = SECOND_IN_MS; // 1 second - for active sync operations

interface UseIntegrationStatusOptions {
  /**
   * Whether to enable polling. When false, polling is paused.
   * Useful for pausing updates when modals are open.
   */
  enablePolling?: boolean;

  /**
   * Polling interval type. Defaults to LONG (30 seconds).
   * Use SHORT (1 second) during active sync operations for real-time updates.
   */
  pollingInterval?: PollingInterval;
}

/**
 * Hook to fetch and manage integration status data
 * Returns the organization's integration status including all connected integrations
 * Polls every 30 seconds by default to keep status up-to-date without excessive backend load
 */
export const useIntegrationStatus = (options: UseIntegrationStatusOptions = {}) => {
  const { enablePolling = true, pollingInterval = PollingInterval.LONG } = options;

  const pollingIntervalMs =
    pollingInterval === PollingInterval.SHORT ? SHORT_POLLING_INTERVAL : LONG_POLLING_INTERVAL;

  return useQuery({
    queryKey: ['integrationStatus'],
    queryFn: getIntegrationStatus,
    // Only poll when enabled, otherwise set to false to disable
    refetchInterval: enablePolling ? pollingIntervalMs : false,
    refetchIntervalInBackground: true, // Don't poll when tab is not focused to reduce load
    staleTime: 10 * SECOND_IN_MS, // Consider data fresh for 10s to reduce unnecessary re-renders
    // Structural sharing ensures React Query only triggers re-renders when data actually changes
    // Without this, every poll creates a new object reference causing unnecessary re-renders
    structuralSharing: true,
  });
};
